import React, { useState, useEffect, useCallback } from 'react';
import { useCluster } from '../context/ClusterContext';
import { useNavigate } from 'react-router-dom';
import { clustersAPI } from '../services/api';
import './MetricsPage.css';

const BAR_COLORS = {
  cpu:    { low: 'var(--color-success-500)', mid: 'var(--accent)', high: 'var(--color-danger-500)' },
  memory: { low: 'var(--brand)', mid: 'var(--accent)', high: 'var(--color-danger-500)' },
};

function BarPct({ pct, type = 'cpu' }) {
  const c = BAR_COLORS[type];
  const color = pct < 60 ? c.low : pct < 85 ? c.mid : c.high;
  return (
    <div className="metrics-bar-wrap">
      <div className="metrics-bar-track">
        <div className="metrics-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
      <span className="metrics-bar-label" style={{ color }}>{pct}%</span>
    </div>
  );
}

export default function MetricsPage() {
  const { selectedCluster } = useCluster();
  const navigate = useNavigate();
  const [nodeMetrics, setNodeMetrics] = useState(null);
  const [podMetrics, setPodMetrics]   = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [podSort, setPodSort]         = useState('cpu');
  const [nsFilter, setNsFilter]       = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    if (!selectedCluster?.is_imported) return;
    setLoading(true); setError('');
    try {
      const [nRes, pRes] = await Promise.all([
        clustersAPI.getNodeMetrics(selectedCluster.id),
        clustersAPI.getPodMetrics(selectedCluster.id, { sort_by: podSort, limit: 50 }),
      ]);
      setNodeMetrics(nRes.data);
      setPodMetrics(pRes.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  // Primitives, not the whole object — see Dashboard.js/Deployments.js for why.
  }, [selectedCluster?.id, selectedCluster?.is_imported, podSort]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(() => load(), 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!selectedCluster) return (
    <div className="resource-page">
      <div className="page-header"><h1>Metrics</h1></div>
      <div className="no-cluster-msg">No cluster selected. <span className="link" onClick={() => navigate('/clusters')}>Select a cluster</span></div>
    </div>
  );

  const nodes = nodeMetrics?.nodes || [];
  const pods  = (podMetrics?.pods || []).filter(p =>
    !nsFilter || p.namespace.toLowerCase().includes(nsFilter.toLowerCase())
  );

  return (
    <div className="resource-page metrics-page">
      <div className="page-header">
        <div>
          <h1>Metrics</h1>
          <div className="resource-cluster-badge">{selectedCluster.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {lastRefresh && <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
          <button className="button-secondary" onClick={load} disabled={loading}>
            {loading ? '⟳ Loading...' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="resource-error">{error}</div>}

      {/* Node metrics */}
      <section className="metrics-section">
        <h2 className="metrics-section-title">Node Resource Usage</h2>
        {nodes.length === 0 ? (
          <div className="metrics-empty">No node metrics available (metrics-server required)</div>
        ) : (
          <div className="metrics-node-grid">
            {nodes.map(n => (
              <div key={n.name} className="metrics-node-card">
                <div className="metrics-node-name">{n.name}</div>
                <div className="metrics-node-rows">
                  <div className="metrics-node-row">
                    <span className="metrics-label">CPU</span>
                    <BarPct pct={n.cpu_percent} type="cpu" />
                    <span className="metrics-value">{n.cpu_cores}m</span>
                  </div>
                  <div className="metrics-node-row">
                    <span className="metrics-label">RAM</span>
                    <BarPct pct={n.memory_percent} type="memory" />
                    <span className="metrics-value">{n.memory_mi >= 1024 ? `${(n.memory_mi/1024).toFixed(1)}Gi` : `${n.memory_mi}Mi`}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pod metrics */}
      <section className="metrics-section">
        <div className="metrics-section-header">
          <h2 className="metrics-section-title">Top Pods</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input className="metrics-ns-filter" placeholder="Filter namespace..." value={nsFilter}
              onChange={e => setNsFilter(e.target.value)} />
            <select className="metrics-sort-select" value={podSort}
              onChange={e => { setPodSort(e.target.value); }}>
              <option value="cpu">Sort by CPU</option>
              <option value="memory">Sort by Memory</option>
            </select>
          </div>
        </div>
        <div className="resource-list">
          <table>
            <thead>
              <tr>
                <th>Pod</th>
                <th>Namespace</th>
                <th>CPU (millicores)</th>
                <th>Memory (Mi)</th>
              </tr>
            </thead>
            <tbody>
              {pods.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No pod metrics</td></tr>
              ) : pods.map((p, i) => (
                <tr key={`${p.namespace}/${p.name}`}>
                  <td><code style={{ fontSize: 12 }}>{p.name}</code></td>
                  <td style={{ color: 'var(--ink-2)', fontSize: 13 }}>{p.namespace}</td>
                  <td>
                    <div className="metrics-pod-bar-row">
                      <div className="metrics-pod-bar">
                        <div className="metrics-pod-bar-fill cpu" style={{ width: `${Math.min(p.cpu_m / 20, 100)}%` }} />
                      </div>
                      <span>{p.cpu_m}m</span>
                    </div>
                  </td>
                  <td>
                    <div className="metrics-pod-bar-row">
                      <div className="metrics-pod-bar">
                        <div className="metrics-pod-bar-fill mem" style={{ width: `${Math.min(p.memory_mi / 100, 100)}%` }} />
                      </div>
                      <span>{p.memory_mi}Mi</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
